// "Live engine monitor" panel — visible during recording. Shows an
// instantaneous level meter, current RPM, current intensity, and a
// dual-line trace over the last ~30 s. Drives confidence that the app
// is actually tracking what the user hears under the hood.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LiveEngineMonitor, LiveEngineConfig, LiveTracePoint, PowertrainMode,
  dbfsToLevel,
} from './recorder/liveEngine';

interface ModeTransition {
  /** ms since recording started. */
  t: number;
  from: PowertrainMode;
  to: PowertrainMode;
}

interface Props {
  /** AnalyserNode tee'd off the recorder's mic source. The panel
   *  manages its own monitor lifecycle around it. */
  analyser: AnalyserNode;
  config: LiveEngineConfig;
  /** Window of trace history to plot, in seconds. */
  windowSec?: number;
}

const DEFAULT_WINDOW_SEC = 30;

export function LiveEnginePanel({ analyser, config, windowSec }: Props) {
  const [rmsDb, setRmsDb] = useState<number>(-160);
  const [peakDb, setPeakDb] = useState<number>(-160);
  const [trace, setTrace] = useState<LiveTracePoint[]>([]);
  const [latest, setLatest] = useState<LiveTracePoint | null>(null);
  // Last few mode transitions, newest first. Surfaced in the hybrid
  // badge so the user sees engine-on / engine-off events as they
  // happen.
  const [transitions, setTransitions] = useState<ModeTransition[]>([]);
  const lastModeRef = useRef<PowertrainMode>('unknown');
  const monitorRef = useRef<LiveEngineMonitor | null>(null);

  useEffect(() => {
    lastModeRef.current = 'unknown';
    setTransitions([]);
    const monitor = new LiveEngineMonitor(
      analyser, config,
      (rms, peak) => { setRmsDb(rms); setPeakDb(peak); },
      (point) => {
        setLatest(point);
        // Pull a fresh snapshot of the ring rather than mutating in
        // place so React notices the change.
        setTrace(monitor.getTrace());
        // Record state transitions (skip the first transition out of
        // 'unknown' — it's a startup artifact, not a real change).
        const prev = lastModeRef.current;
        if (point.mode !== prev) {
          if (prev !== 'unknown') {
            setTransitions((tx) => [
              { t: point.t, from: prev, to: point.mode },
              ...tx,
            ].slice(0, 8));
          }
          lastModeRef.current = point.mode;
        }
      },
    );
    monitor.start();
    monitorRef.current = monitor;
    return () => {
      monitor.stop();
      monitorRef.current = null;
    };
  }, [analyser, config.numCylinders, config.numStrokes, config.powertrainKind]);

  const win = windowSec ?? DEFAULT_WINDOW_SEC;

  const modeNow: PowertrainMode = latest?.mode ?? 'unknown';

  return (
    <div className="panel" style={{ marginTop: 0 }}>
      <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="row" style={{ alignItems: 'center', gap: '0.6rem' }}>
          <h3 style={{ margin: 0 }}>Live engine monitor</h3>
          {config.powertrainKind === 'hybrid' && (
            <ModeBadge mode={modeNow} prominent />
          )}
        </div>
        <span className="muted" style={{ fontSize: '0.78rem' }}>
          {config.powertrainKind === 'ev'
            ? 'EV (motor only)'
            : `${config.numCylinders}-cyl, ${config.numStrokes}-stroke`}
          {config.powertrainKind === 'hybrid' && ' (hybrid)'}
          {' · '}window {win}s
        </span>
      </div>
      {config.powertrainKind === 'hybrid' && transitions.length > 0 && (
        <ModeTransitionStrip transitions={transitions} />
      )}

      <div style={{ marginTop: '0.6rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <BigMetric
          label="RPM (live)"
          value={latest?.rpm != null ? Math.round(latest.rpm).toString() : '—'}
          tone={latest && latest.rpm != null
            ? (latest.rpmConfidence >= 0.5 ? 'var(--accent)'
              : latest.rpmConfidence >= 0.25 ? '#f59e0b'
              : 'var(--muted)')
            : 'var(--muted)'}
          subtitle={latest?.rpm != null
            ? `confidence ${(latest.rpmConfidence * 100).toFixed(0)}%`
            : 'no lock'}
        />
        <BigMetric
          label="Intensity"
          value={Number.isFinite(rmsDb) && rmsDb > -160 ? `${rmsDb.toFixed(0)} dB` : '—'}
          tone="var(--fg)"
          subtitle={`peak ${Number.isFinite(peakDb) && peakDb > -160 ? peakDb.toFixed(0) : '—'} dB`}
        />
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="muted" style={{ fontSize: '0.78rem', marginBottom: 3 }}>
            Level meter
          </div>
          <LevelMeter rmsDb={rmsDb} peakDb={peakDb} />
        </div>
      </div>

      <div style={{ marginTop: '0.7rem' }}>
        <DualTrace points={trace} windowSec={win} />
      </div>

      <div className="muted" style={{ fontSize: '0.78rem', marginTop: 6 }}>
        RPM line tracks engine speed; intensity line tracks acoustic
        effort. Both rise on a free rev; under load (driving uphill,
        accelerating) intensity rises faster than RPM — that divergence
        is what the analysis exploits to characterize the engine.
      </div>
    </div>
  );
}

// ---- Big-number metric tile ------------------------------------------

function BigMetric({
  label, value, tone, subtitle,
}: {
  label: string; value: string; tone: string; subtitle: string;
}) {
  return (
    <div style={{ minWidth: 130 }}>
      <div className="muted" style={{ fontSize: '0.78rem' }}>{label}</div>
      <div style={{ fontSize: '2.2rem', fontWeight: 700, lineHeight: 1.05, color: tone }}>
        {value}
      </div>
      <div className="muted" style={{ fontSize: '0.7rem' }}>{subtitle}</div>
    </div>
  );
}

// ---- Horizontal level meter ------------------------------------------

function LevelMeter({ rmsDb, peakDb }: { rmsDb: number; peakDb: number }) {
  const rmsLvl = dbfsToLevel(rmsDb);
  const peakLvl = dbfsToLevel(peakDb);
  // Color zones: -60..-18 → green, -18..-6 → amber, -6..0 → red.
  // Map back to fractions: 0..0.7 green, 0.7..0.9 amber, 0.9..1 red.
  return (
    <div style={{
      position: 'relative',
      height: 22, borderRadius: 4,
      background: 'var(--panel-2)',
      border: '1px solid var(--border)',
      overflow: 'hidden',
    }}>
      {/* Color-zone backdrop */}
      <div style={{
        position: 'absolute', inset: 0, display: 'flex',
        opacity: 0.18,
      }}>
        <div style={{ flex: 0.7, background: 'var(--ok)' }} />
        <div style={{ flex: 0.2, background: '#f59e0b' }} />
        <div style={{ flex: 0.1, background: 'var(--danger)' }} />
      </div>
      {/* RMS bar */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: 0,
        width: `${(rmsLvl * 100).toFixed(1)}%`,
        background: peakLvl >= 0.95 ? 'var(--danger)'
                  : peakLvl >= 0.7 ? '#f59e0b'
                  : 'var(--accent)',
        transition: 'width 33ms linear',
      }} />
      {/* Peak indicator */}
      <div style={{
        position: 'absolute', top: 1, bottom: 1,
        left: `calc(${(peakLvl * 100).toFixed(1)}% - 1px)`,
        width: 2,
        background: peakLvl >= 0.95 ? 'var(--danger)' : 'var(--fg)',
        opacity: 0.85,
      }} />
    </div>
  );
}

// ---- Dual-line trace --------------------------------------------------

function DualTrace({
  points, windowSec,
}: { points: LiveTracePoint[]; windowSec: number }) {
  const W = 720;
  const H = 140;
  const margin = { l: 38, r: 36, t: 6, b: 16 };
  const innerW = W - margin.l - margin.r;
  const innerH = H - margin.t - margin.b;

  if (points.length < 2) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }}>
        <text x={W / 2} y={H / 2} textAnchor="middle" fill="var(--muted)" fontSize="11">
          collecting samples…
        </text>
      </svg>
    );
  }

  const tNow = points[points.length - 1].t;
  const tMin = tNow - windowSec * 1000;
  const visible = points.filter((p) => p.t >= tMin);
  if (visible.length < 2) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H, display: 'block' }}>
        <text x={W / 2} y={H / 2} textAnchor="middle" fill="var(--muted)" fontSize="11">
          warming up…
        </text>
      </svg>
    );
  }

  // RPM y-axis: lock to 0..auto with a sensible floor (idle ~600).
  const rpmMax = Math.max(
    1500,
    ...visible.map((p) => p.rpm ?? 0),
  );
  const rpmCeil = Math.ceil(rpmMax / 500) * 500;

  // Intensity y-axis: dBFS, bottom of useful range to the most-recent
  // peak. Pad the top by 3 dB so a sudden rev doesn't flatten against
  // the ceiling.
  const intMin = -60;
  const intMax = Math.min(0, Math.max(...visible.map((p) => p.intensityDbfs)) + 3);
  const intRange = Math.max(1, intMax - intMin);

  const xOf = (t: number) =>
    margin.l + ((t - tMin) / (windowSec * 1000)) * innerW;
  const yRpm = (r: number) =>
    margin.t + (1 - r / rpmCeil) * innerH;
  const yInt = (db: number) =>
    margin.t + (1 - (db - intMin) / intRange) * innerH;

  // Build RPM path. Break at null (RPM lost lock) so the line goes
  // dotted instead of jumping.
  const rpmSegments: string[] = [];
  let current: string[] = [];
  for (const p of visible) {
    if (p.rpm == null) {
      if (current.length > 1) rpmSegments.push(current.join(' '));
      current = [];
      continue;
    }
    const cmd = current.length === 0 ? 'M' : 'L';
    current.push(`${cmd}${xOf(p.t).toFixed(1)},${yRpm(p.rpm).toFixed(1)}`);
  }
  if (current.length > 1) rpmSegments.push(current.join(' '));

  // Intensity is always continuous.
  const intPath = visible
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(p.t).toFixed(1)},${yInt(p.intensityDbfs).toFixed(1)}`)
    .join(' ');

  // Y-axis grid + ticks (RPM on the left, intensity on the right).
  const rpmTicks = [0, rpmCeil * 0.25, rpmCeil * 0.5, rpmCeil * 0.75, rpmCeil];
  const intTicks = [intMin, (intMin + intMax) / 2, intMax];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
         style={{ width: '100%', height: H, display: 'block', background: 'var(--panel-2)', borderRadius: 4 }}>
      {/* Horizontal grid */}
      {rpmTicks.map((r) => (
        <line key={`rg-${r}`}
          x1={margin.l} x2={W - margin.r}
          y1={yRpm(r)} y2={yRpm(r)}
          stroke="var(--border)" strokeDasharray="2 3" strokeWidth={0.5} />
      ))}
      {/* RPM y-axis labels (left) */}
      {rpmTicks.map((r) => (
        <text key={`rt-${r}`} x={margin.l - 4} y={yRpm(r) + 3}
              fontSize="9" fill="var(--accent)" textAnchor="end">{r}</text>
      ))}
      {/* Intensity y-axis labels (right) */}
      {intTicks.map((db) => (
        <text key={`it-${db.toFixed(1)}`} x={W - margin.r + 4} y={yInt(db) + 3}
              fontSize="9" fill="var(--muted)" textAnchor="start">
          {db.toFixed(0)}
        </text>
      ))}
      {/* Intensity area for visual weight (faded) */}
      <path d={`${intPath} L${xOf(visible[visible.length - 1].t).toFixed(1)},${(margin.t + innerH).toFixed(1)} L${xOf(visible[0].t).toFixed(1)},${(margin.t + innerH).toFixed(1)} Z`}
            fill="var(--muted)" opacity={0.12} />
      {/* Intensity line */}
      <path d={intPath} stroke="var(--muted)" strokeWidth={1.2} fill="none" opacity={0.85} />
      {/* RPM line(s) — split when confidence dropped */}
      {rpmSegments.map((d, i) => (
        <path key={i} d={d} stroke="var(--accent)" strokeWidth={1.8} fill="none" />
      ))}
      {/* Axis labels (corner) */}
      <text x={margin.l} y={H - 4} fontSize="9" fill="var(--accent)">RPM</text>
      <text x={W - margin.r} y={H - 4} fontSize="9" fill="var(--muted)" textAnchor="end">intensity (dBFS)</text>
    </svg>
  );
}

// ---- Powertrain-mode badge + transition strip ------------------------

const MODE_PRESETS: Record<PowertrainMode, { label: string; tone: string; bg: string }> = {
  engine:  { label: 'ENGINE',  tone: 'var(--accent)', bg: 'rgba(99, 102, 241, 0.18)' },
  ev:      { label: 'EV',      tone: '#10b981',       bg: 'rgba(16, 185, 129, 0.18)' },
  off:     { label: 'OFF',     tone: 'var(--muted)',  bg: 'rgba(148, 163, 184, 0.12)' },
  unknown: { label: '…',       tone: 'var(--muted)',  bg: 'transparent' },
};

function ModeBadge({ mode, prominent }: { mode: PowertrainMode; prominent?: boolean }) {
  const p = MODE_PRESETS[mode];
  const fontSize = prominent ? '1.05rem' : '0.78rem';
  const padding = prominent ? '4px 12px' : '2px 6px';
  return (
    <span
      title={
        mode === 'engine' ? 'Engine running — firing-rate harmonics detected'
        : mode === 'ev' ? 'EV mode — audio present but no firing harmonics'
        : mode === 'off' ? 'Powertrain quiet — at or below noise floor'
        : 'Determining mode…'
      }
      style={{
        display: 'inline-block',
        padding,
        borderRadius: 999,
        background: p.bg,
        color: p.tone,
        border: `1px solid ${p.tone}`,
        fontSize,
        fontWeight: 700,
        letterSpacing: '0.05em',
      }}
    >
      {p.label}
    </span>
  );
}

function ModeTransitionStrip({ transitions }: { transitions: ModeTransition[] }) {
  // Format relative time like "0:03 ago".
  const now = Date.now();
  const startedAt = transitions.length > 0
    ? now - transitions[0].t  // approximate; close enough for relative display
    : now;
  return (
    <div className="muted" style={{
      fontSize: '0.78rem', marginTop: 4,
      display: 'flex', flexWrap: 'wrap', gap: '0.6rem',
    }}>
      <span>Recent transitions:</span>
      {transitions.map((tx, i) => {
        const ago = ((Date.now() - (startedAt + tx.t)) / 1000);
        const minutes = Math.floor(ago / 60);
        const seconds = Math.floor(ago % 60);
        const agoStr = minutes > 0 ? `${minutes}:${String(seconds).padStart(2, '0')}` : `${seconds}s`;
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <ModeBadge mode={tx.from} />
            <span>→</span>
            <ModeBadge mode={tx.to} />
            <span>{agoStr} ago</span>
          </span>
        );
      })}
    </div>
  );
}
